import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Property } from "../../models/property.model";
import { PropertyImage } from "../../models/propertyImage.model";
import GalleryModal from "./GalleryModal";
import PropertyGallery from "./PropertyGallery";
interface PropertyDetailProps {
  property: Property | null;
}

// Removed hard-coded sample images and amenities list; real data comes from API.

const PropertyDetail: React.FC<PropertyDetailProps> = ({ property }) => {
  const navigate = useNavigate();
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Property Gallery Modal State
  const [propertyGalleryModal, setPropertyGalleryModal] = useState<{
    visible: boolean;
    images: Array<{
      id: number;
      imageUrl: string;
      type: string;
      name: string;
      category?: string;
    }>;
    initialImageIndex: number;
    propertyData?: any;
  }>({
    visible: false,
    images: [],
    initialImageIndex: 0,
    propertyData: null,
  });
  const getGalleryImages = (
    property: Property | null
  ): (string | PropertyImage)[] => {
    if (!property) return [];
    const raw = property as any;

    const candidates = [raw.ImageDtos, raw.imageDtos];

    for (const c of candidates) {
      if (Array.isArray(c) && c.length) return c as (string | PropertyImage)[];
    }

    if (raw.imageUrl) return [raw.imageUrl];
    return [];
  };

  // Helper to extract image URLs from property
  const getPropertyImageUrls = (property: Property | null): string[] => {
    if (!property) return [];

    const galleryImages = getGalleryImages(property);
    return galleryImages
      .map((img) => {
        if (typeof img === "string") return img;
        if (typeof img === "object" && img.imageUrl) return img.imageUrl;
        // Handle base64 images with any type
        const imgAny = img as any;
        if (typeof img === "object" && imgAny.imageByte && imgAny.contentType) {
          return `data:${imgAny.contentType};base64,${imgAny.imageByte}`;
        }
        return "";
      })
      .filter(Boolean);
  };

  // Open Property Gallery Modal
  const openPropertyGallery = (clickedImageIndex: number = 0) => {
    if (!property) return;

    const propertyImages = getPropertyImageUrls(property);
    const propertyName = property.name || "Property";
    const propertyType = property.propertyType?.name || "Property";

    console.log("openPropertyGallery - property:", property);
    console.log("openPropertyGallery - extracted images:", propertyImages);

    // Create image objects for the modal
    const propertyImageData: Array<{
      id: number;
      imageUrl: string;
      type: string;
      name: string;
      category?: string;
    }> = propertyImages.map((imageUrl, imgIdx) => ({
      id: imgIdx,
      imageUrl,
      type: propertyType,
      name: propertyName,
      category: "Property Image",
    }));

    setPropertyGalleryModal({
      visible: true,
      images: propertyImageData,
      initialImageIndex: clickedImageIndex,
      propertyData: property,
    });
  };

  // Close Property Gallery Modal
  const closePropertyGallery = () => {
    setPropertyGalleryModal({
      visible: false,
      images: [],
      initialImageIndex: 0,
      propertyData: null,
    });
  };

  useEffect(() => {
    console.log(
      "PropertyDetail component mounted or property changed:",
      property
    );
  }, [property]);
  if (!property) return <div className="text-center">Loading...</div>;

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <i key={i} className="bi bi-star-fill text-warning star-md"></i>
      );
    }
    if (halfStar) {
      stars.push(
        <i key="half" className="bi bi-star-half text-warning star-md"></i>
      );
    }
    while (stars.length < 5) {
      stars.push(
        <i
          key={"empty" + stars.length}
          className="bi bi-star text-warning star-md"
        ></i>
      );
    }
    return stars;
  };

  // Helper to get only first two lines (approx) of description
  const getShortDescription = (desc: string) => {
    const lines = desc.split("\n");
    if (lines.length > 2) {
      return lines.slice(0, 2).join(" ");
    }
    // Fallback: limit by words if no line breaks
    return (
      desc.split(" ").slice(0, 25).join(" ") +
      (desc.split(" ").length > 25 ? "..." : "")
    );
  };

  return (
    <>
      <div className="card border-0 shadow-sm vp-card">
        <div className="row g-0">
          {/* Images Section */}
          <div className="col-lg-6">
            <div className="vp-gallery-wrapper">
              <PropertyGallery
                images={getGalleryImages(property)}
                height={400}
                onMainImageClick={() => openPropertyGallery(0)}
              />
            </div>
          </div>

          {/* Details Section */}
          <div className="col-lg-5">
            <div className="d-flex flex-column gap-2 h-100">
              {/* Rating Section */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="rating-badge">
                  {property.rating?.toFixed(1) || "9.2/10"}
                </span>
                <span className="rating-text">Ratings</span>
                <div className="d-flex align-items-center gap-1 star-group">
                  {renderStars(property.rating || 4)}
                </div>
              </div>

              {/* Property Title & Location */}
              <div className="d-flex flex-column gap-1">
                <h2 className="vp-title mb-0">{property.name}</h2>
                <div className="property-location-info">
                  <span><i className="pi pi-building me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.propertyType?.name}</span>
                  <span className="dot">•</span>
                  <span><i className="pi pi-map-marker me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.location?.name}</span>
                  {property.location?.zone?.name && (
                    <>
                      <span className="dot">•</span>
                      <span><i className="pi pi-map me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.location.zone.name}</span>
                    </>
                  )}
                  {property.city?.name && (
                    <>
                      <span className="dot">•</span>
                      <span><i className="pi pi-globe me-1" style={{ fontSize: '0.8rem', color: '#87CEEB' }}></i>{property.city.name}</span>
                    </>
                  )}
                </div>

                {property.roomTypes && property.roomTypes.length > 0 && (
                  <ul className="vp-rooms list-unstyled mb-0">
                    {property.roomTypes.slice(0, 6).map((rt: any) => (
                      <li key={rt.id || rt.name} className="room-pill">
                        <i className="bi bi-house-door me-2" />
                        {rt.name}
                      </li>
                    ))}
                    {property.roomTypes.length > 6 && (
                      <li className="room-pill more">
                        +{property.roomTypes.length - 6} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
              {/* About Property Section */}
              {property.description && (
                <div className="border-top pt-2">
                  <h5 className="about-title">{`About ${property.name}`}</h5>
                  <div className="about-text">
                    {getShortDescription(property.description)}
                  </div>
                  {/* <span className="text-muted small">
                    ({Math.floor(Math.random() * 500) + 100} reviews)
                  </span> */}
                  {property.description.length > 200 && (
                    <button
                      className="btn btn-link btn-sm p-0 text-primary mt-1 link-pointer"
                      onClick={() => setShowAboutModal(true)}
                    >
                      Show more
                    </button>
                  )}
                </div>
              )}

              {/* Address */}
              {/* {property.address && (
                <div className="mt-auto">
                  <div className="d-flex align-items-start">
                    <i className="bi bi-pin-map me-2 mt-1"></i>
                    <small className="text-muted">{property.address}</small>
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>

      {/* About Property Modal - simplified */}
      <Dialog
        header="About Property"
        visible={showAboutModal}
        style={{ width: "70vw", maxWidth: "600px" }}
        onHide={() => setShowAboutModal(false)}
        modal
        draggable={false}
        resizable={false}
      >
        <div className="text-muted vp-modal-text">{property.description}</div>
      </Dialog>

      {/* Property Gallery Modal */}
      <GalleryModal
        visible={propertyGalleryModal.visible}
        onHide={closePropertyGallery}
        images={propertyGalleryModal.images}
        initialImageIndex={propertyGalleryModal.initialImageIndex}
        data={propertyGalleryModal.propertyData}
        mode="property"
      />
    </>
  );
};

export default PropertyDetail;
